/**
 * StepIndicator Component - Foundational Primitive #7 (ProgressStepper)
 *
 * Progress stepper showing current step in multi-step flows. Uses CSS primitive classes from:
 * - 07-progress-stepper.css for .stepper styling
 *
 * Features:
 * - Gold/maroon/gray step indicators (per DO THIS/NOT THAT guidelines)
 * - Completed steps (gold checkmark), active step (maroon), future steps (gray)
 * - Horizontal connector lines between steps
 * - Responsive (compact on mobile)
 *
 * All styling uses CSS primitives + CSS variables (no hardcoding).
 */

import React, { useState, useEffect } from 'react';
import '../../theme-components/07-progress-stepper.css';

export interface Step {
  id: number;
  title: string;
  description: string;
  completed: boolean;
  enabled: boolean;
  hidden?: boolean;
}

export interface StepIndicatorProps {
  steps: Step[];
  currentStep: number;
  onStepClick?: (stepId: number) => void;
  className?: string;
  forceCompact?: boolean;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({
  steps,
  currentStep,
  onStepClick,
  className = '',
  forceCompact = false
}) => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 700);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const isCompact = forceCompact || isMobile;
  const visibleSteps = steps.filter(step => !step.hidden);

  const handleStepClick = (step: Step) => {
    if (step.enabled && onStepClick) {
      onStepClick(step.id);
    }
  };

  // Get CSS primitive state class
  const getStepStateClass = (step: Step) => {
    const isActive = currentStep === step.id;

    if (step.completed) {
      return 'stepper-step--completed';
    } else if (isActive) {
      return 'stepper-step--active';
    } else {
      return 'stepper-step--pending';
    }
  };

  // Get connector state class
  const getConnectorStateClass = (step: Step) => {
    return step.completed ? 'stepper-connector--completed' : '';
  };

  return (
    <div className={`stepper ${className}`}>
      {visibleSteps.map((step, index) => {
        const stateClass = getStepStateClass(step);
        const isActive = currentStep === step.id;

        return (
          <React.Fragment key={step.id}>
            {/* Step */}
            <div
              className={`stepper-step ${stateClass} ${step.enabled && onStepClick ? 'cursor-pointer' : 'cursor-default'}`}
              onClick={() => handleStepClick(step)}
            >
              {/* Circle */}
              <div className="stepper-circle">
                {step.completed ? (
                  <svg className="stepper-check" viewBox="0 0 16 16">
                    <path
                      d="M13.5 4.5l-7 7L3 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      fill="none"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : (
                  step.id
                )}
              </div>

              {/* Step Info (labels next to circle) */}
              {(!isCompact || isActive) && (
                <div className="stepper-step-info">
                  <div className="stepper-label">{step.title}</div>
                  {!isCompact && (
                    <div className="stepper-sublabel">{step.description}</div>
                  )}
                </div>
              )}
            </div>

            {/* Connector */}
            {index < visibleSteps.length - 1 && (
              <div className={`stepper-connector ${getConnectorStateClass(step)}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default StepIndicator;
